/**
 * Weekly Coaching Service
 * 
 * SPRINT B: Manages weekly coaching generation and caching
 * 
 * Rules:
 * - Runs Monday 00:10 local time
 * - Auto-runs if missed (when app opened on Monday)
 * - Cached until next Monday
 * - Messages selected from JSON banks
 * 
 * P0 BUG FIX: Cache is now keyed by ISO week (year + week number) to prevent
 * mid-week regeneration. Coaching is generated once per ISO week and never
 * regenerates due to daily logging, partial day closure, or screen reloads.
 */

import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { generateWeeklyCoaching, type WeeklyCoachingResult } from './behaviourEngine.service';
import { getUserProfile } from './userProfile.service';
import {
  fetchWeeklyCoachingFromBackend,
  mapBackendToFrontendFormat,
} from './backendCoaching.service';

dayjs.extend(isoWeek);

// Single-flight gate — prevents concurrent callers from triggering duplicate
// coaching generation and duplicate backend API calls during boot
const inFlightCoaching = new Map<string, Promise<WeeklyCoachingResult | null>>();

const WEEKLY_COACHING_CACHE_KEY_PREFIX = 'coretrak.weekly_coaching.cache';

function getWeeklyCoachingCacheKey(userId: string): string {
  return `${WEEKLY_COACHING_CACHE_KEY_PREFIX}.${userId}`;
}

export interface CachedWeeklyCoaching {
  userId: string;
  isoWeekId: string;
  weekStartDate: string;
  generatedAt: number;
  coaching: WeeklyCoachingResult;
}

export function getMondayDate(date?: string): string {
  const targetDate = date ? dayjs(date) : dayjs();
  return targetDate.startOf('isoWeek').format('YYYY-MM-DD');
}

export function getIsoWeekId(date?: string): string {
  const targetDate = date ? dayjs(date) : dayjs();
  const year = targetDate.isoWeekYear();
  const week = targetDate.isoWeek();
  return `${year}-W${week.toString().padStart(2, '0')}`;
}

export function shouldRunWeeklyEvaluator(): boolean {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const hour = now.getHours();
  const minute = now.getMinutes();
  if (dayOfWeek === 1) {
    if (hour > 0 || (hour === 0 && minute >= 10)) {
      return true;
    }
  }
  return false;
}

export function getCachedWeeklyCoaching(userId: string): CachedWeeklyCoaching | null {
  try {
    const cacheKey = getWeeklyCoachingCacheKey(userId);
    const currentIsoWeekId = getIsoWeekId();
    const currentMonday = getMondayDate();

    if (import.meta.env.DEV) {
      console.log('[P0 VERIFICATION] getCachedWeeklyCoaching called', {
        userId,
        currentIsoWeekId,
        currentMonday,
        cacheKey,
        timestamp: new Date().toISOString(),
      });
    }

    const cached = localStorage.getItem(cacheKey);
    if (!cached) {
      if (import.meta.env.DEV) {
        console.log('[P0 VERIFICATION] No cached coaching found in localStorage');
      }
      return null;
    }

    const data = JSON.parse(cached) as CachedWeeklyCoaching;

    if (import.meta.env.DEV) {
      console.log('[P0 VERIFICATION] Found cached coaching', {
        cachedUserId: data.userId,
        cachedIsoWeekId: data.isoWeekId,
        cachedWeekStartDate: data.weekStartDate,
        cachedGeneratedAt: new Date(data.generatedAt).toISOString(),
        ageMinutes: Math.round((Date.now() - data.generatedAt) / 60000),
      });
    }

    if (data.userId && data.userId !== userId) {
      if (import.meta.env.DEV) {
        console.log('[P0 VERIFICATION] ❌ Cache invalidated: userId mismatch', {
          cachedUserId: data.userId,
          requestedUserId: userId,
        });
      }
      localStorage.removeItem(cacheKey);
      return null;
    }

    if (data.isoWeekId && data.isoWeekId !== currentIsoWeekId) {
      if (import.meta.env.DEV) {
        console.log('[P0 VERIFICATION] ❌ Cache invalidated: ISO week changed', {
          cachedIsoWeekId: data.isoWeekId,
          currentIsoWeekId,
          reason: 'ISO week transition - new coaching will be generated',
        });
      }
      localStorage.removeItem(cacheKey);
      return null;
    }

    if (!data.isoWeekId) {
      if (import.meta.env.DEV) {
        console.log('[P0 VERIFICATION] Legacy cache entry (no isoWeekId), checking weekStartDate');
      }
      if (data.weekStartDate !== currentMonday) {
        if (import.meta.env.DEV) {
          console.log('[P0 VERIFICATION] ❌ Legacy cache invalidated: weekStartDate mismatch', {
            cachedWeekStartDate: data.weekStartDate,
            currentMonday,
          });
        }
        localStorage.removeItem(cacheKey);
        return null;
      }
    }

    if (import.meta.env.DEV) {
      console.log('[P0 VERIFICATION] ✅ Cache HIT - returning cached coaching', {
        isoWeekId: data.isoWeekId || 'legacy',
        weekStartDate: data.weekStartDate,
        coachingMessageCount: {
          behaviour: data.coaching.behaviour.length,
          nutrition_l2: data.coaching.nutrition_l2.length,
          body_stats: data.coaching.body_stats.length,
          exercise: data.coaching.exercise.length,
        },
        verification: 'Coaching will NOT regenerate - same ISO week',
      });
    }

    return data;
  } catch (error) {
    console.error('[P0 VERIFICATION] ❌ Failed to read cache:', error);
    return null;
  }
}

export function cacheWeeklyCoaching(userId: string, coaching: WeeklyCoachingResult, weekStartDate: string): void {
  try {
    const cacheKey = getWeeklyCoachingCacheKey(userId);
    const isoWeekId = getIsoWeekId(weekStartDate);
    const cache: CachedWeeklyCoaching = {
      userId,
      isoWeekId,
      weekStartDate,
      generatedAt: Date.now(),
      coaching,
    };

    if (import.meta.env.DEV) {
      console.log('[P0 VERIFICATION] ✅ Caching weekly coaching', {
        userId,
        isoWeekId,
        weekStartDate,
        cacheKey,
        generatedAt: new Date(cache.generatedAt).toISOString(),
        coachingSummary: {
          behaviour: coaching.behaviour.length,
          nutrition_l2: coaching.nutrition_l2.length,
          body_stats: coaching.body_stats.length,
          exercise: coaching.exercise.length,
        },
        verification: 'This coaching will be reused for entire ISO week',
      });
    }

    localStorage.setItem(cacheKey, JSON.stringify(cache));

    if (import.meta.env.DEV) {
      console.log('[P0 VERIFICATION] ✅ Cache saved successfully', {
        isoWeekId,
        verification: 'Future requests for this ISO week will return cached coaching',
      });
    }
  } catch (error) {
    console.error('[P0 VERIFICATION] ❌ Failed to cache:', error);
  }
}

export async function generateAndCacheWeeklyCoaching(
  userId: string,
): Promise<WeeklyCoachingResult | null> {
  const currentMonday = getMondayDate();
  const currentIsoWeekId = getIsoWeekId();

  if (import.meta.env.DEV) {
    console.log('[P0 VERIFICATION] ========== GENERATING NEW WEEKLY COACHING ==========', {
      userId,
      currentIsoWeekId,
      currentMonday,
      timestamp: new Date().toISOString(),
      verification: 'This should happen ONCE per ISO week only',
    });
  }

  const profile = await getUserProfile(userId);
  if (profile?.notifications_enabled === false) {
    if (import.meta.env.DEV) {
      console.log('[P0 VERIFICATION] ⏸️ Notifications disabled, skipping generation');
    }
    return null;
  }

  let coaching: WeeklyCoachingResult | null = null;
  try {
    if (import.meta.env.DEV) {
      console.log('[P0 VERIFICATION] Attempting to fetch coaching from backend API...', {
        userId,
        weekStartDate: currentMonday,
      });
    }

    const backendResponse = await fetchWeeklyCoachingFromBackend(currentMonday, false);
    coaching = mapBackendToFrontendFormat(backendResponse);

    if (import.meta.env.DEV) {
      console.log('[P0 VERIFICATION] ✅ Backend API coaching received and mapped', {
        isoWeekId: currentIsoWeekId,
        route: backendResponse.route,
        coachingSummary: {
          behaviour: coaching.behaviour.length,
          nutrition_l2: coaching.nutrition_l2.length,
          body_stats: coaching.body_stats.length,
          exercise: coaching.exercise.length,
        },
        exerciseMessages: coaching.exercise,
        backendRecommendations: backendResponse.result.recommendations,
        backendNextActions: backendResponse.result.next_actions,
      });
    }

    if (coaching.exercise.length === 0) {
      if (import.meta.env.DEV) {
        console.warn('[BUG FIX #12] ⚠️ Exercise coaching is empty after backend mapping', {
          userId,
          weekStartDate: currentMonday,
          isoWeekId: currentIsoWeekId,
          backendRoute: backendResponse.route,
          backendRecommendations: backendResponse.result.recommendations,
          backendNextActions: backendResponse.result.next_actions,
          backendSummary: backendResponse.result.summary,
          mappedCoaching: {
            behaviour: coaching.behaviour.length,
            nutrition_l2: coaching.nutrition_l2.length,
            body_stats: coaching.body_stats.length,
            exercise: coaching.exercise.length,
          },
          note: 'Backend should generate exercise coaching when workout_summary counts are present. Check backend prompt and keyword mapping.',
        });
      }
    } else {
      if (import.meta.env.DEV) {
        console.log('[BUG FIX #12] ✅ Exercise coaching successfully extracted from backend', {
          userId,
          exerciseCount: coaching.exercise.length,
          exerciseMessages: coaching.exercise,
        });
      }
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[P0 VERIFICATION] ⚠️ Backend API failed, falling back to local generation', {
        error: error instanceof Error ? error.message : error,
      });
      console.log('[P0 VERIFICATION] Calling local generateWeeklyCoaching...', {
        userId,
        weekStartDate: currentMonday,
      });
    }

    coaching = await generateWeeklyCoaching(userId, currentMonday);

    if (import.meta.env.DEV) {
      console.log('[P0 VERIFICATION] ✅ Local coaching generated successfully', {
        isoWeekId: currentIsoWeekId,
        coachingSummary: {
          behaviour: coaching.behaviour.length,
          nutrition_l2: coaching.nutrition_l2.length,
          body_stats: coaching.body_stats.length,
          exercise: coaching.exercise.length,
        },
        exerciseMessages: coaching.exercise,
      });
    }

    if (coaching.exercise.length === 0) {
      if (import.meta.env.DEV) {
        console.warn('[BUG FIX #12] ⚠️ Exercise coaching is empty after local generation', {
          userId,
          weekStartDate: currentMonday,
          isoWeekId: currentIsoWeekId,
          mappedCoaching: {
            behaviour: coaching.behaviour.length,
            nutrition_l2: coaching.nutrition_l2.length,
            body_stats: coaching.body_stats.length,
            exercise: coaching.exercise.length,
          },
          note: 'This may indicate no workouts found for the week or message bank is empty. Check workout entries and message banks.',
        });
      }
    } else {
      if (import.meta.env.DEV) {
        console.log('[BUG FIX #12] ✅ Exercise coaching successfully generated locally', {
          userId,
          exerciseCount: coaching.exercise.length,
          exerciseMessages: coaching.exercise,
        });
      }
    }
  }

  if (!coaching) {
    console.error('[P0 VERIFICATION] ❌ Failed to generate coaching from both backend and local');
    return null;
  }

  if (import.meta.env.DEV) {
    console.log('[P0 VERIFICATION] ✅ Coaching generated successfully', {
      isoWeekId: currentIsoWeekId,
      coachingSummary: {
        behaviour: coaching.behaviour.length,
        nutrition_l2: coaching.nutrition_l2.length,
        body_stats: coaching.body_stats.length,
        exercise: coaching.exercise.length,
      },
      verification: 'This coaching will be cached and reused for entire ISO week',
    });
  }

  cacheWeeklyCoaching(userId, coaching, currentMonday);

  if (import.meta.env.DEV) {
    console.log('[P0 VERIFICATION] ========== GENERATION COMPLETE ==========', {
      isoWeekId: currentIsoWeekId,
      verification: 'Future requests for this ISO week will return cached version',
    });
  }

  return coaching;
}

export async function getWeeklyCoaching(
  userId: string,
): Promise<WeeklyCoachingResult | null> {
  const currentIsoWeekId = getIsoWeekId();
  const currentMonday = getMondayDate();
  const isMonday = shouldRunWeeklyEvaluator();

  if (import.meta.env.DEV) {
    console.log('[P0 VERIFICATION] ========== getWeeklyCoaching called ==========', {
      userId,
      currentIsoWeekId,
      currentMonday,
      isMonday,
      currentTime: new Date().toISOString(),
      trigger: 'User opened Weekly Summary or coaching requested',
    });
  }

  const cached = getCachedWeeklyCoaching(userId);
  if (cached) {
    if (import.meta.env.DEV) {
      console.log('[P0 VERIFICATION] ✅ ACCEPTANCE CRITERIA MET: Returning cached coaching', {
        isoWeekId: cached.isoWeekId,
        verification: [
          '✅ Coaching does NOT regenerate when screen reopened',
          '✅ Same coaching returned for entire ISO week',
          '✅ Mid-week logging will NOT trigger regeneration',
        ],
      });
    }
    return cached.coaching;
  }

  const flightKey = `${userId}|${currentIsoWeekId}`;
  const existing = inFlightCoaching.get(flightKey);
  if (existing) {
    if (import.meta.env.DEV) {
      console.log('[P0 VERIFICATION] ⏳ Coaching generation already in-flight — awaiting existing promise');
    }
    return existing;
  }

  if (import.meta.env.DEV) {
    console.log('[BUG FIX #12] No cache found, generating coaching on-demand', {
      reason: 'No cached coaching for current ISO week - generating on-demand to ensure users see coaching',
      isoWeekId: currentIsoWeekId,
      isMonday,
      verification: 'Coaching will be cached after generation and reused for entire ISO week',
    });
  }

  const p = (async () => {
    try {
      return await generateAndCacheWeeklyCoaching(userId);
    } finally {
      inFlightCoaching.delete(flightKey);
    }
  })();

  inFlightCoaching.set(flightKey, p);
  return p;
}
